/**
 * «LinkedIn paylaşım görseli» — the second, OPTIONAL panel, opened from the editor.
 *
 * WHAT IT IS NOT
 * --------------
 * It is not the certificate designer. That panel is untouched: its tools, its layout and its
 * behaviour are exactly what they were, and nothing here can change a drawing, a release or an
 * issued record. What an author arranges on this canvas is the picture a LinkedIn post carries —
 * and only that.
 *
 * WHERE IT IS OPENED FROM. The editor's «LinkedIn görseli» button, after the certificate has been
 * prepared. It was briefly a numbered step in the preparation wizard as well; that step was taken
 * back out, because a ninth chip in the stepper moved the preparation screen's own layout and the
 * brief allowed either entrance. One entrance, one component, one saved arrangement.
 *
 * THE CERTIFICATE ON THE CANVAS IS THE REAL DRAWING
 * -------------------------------------------------
 * `artwork` is the editor's own working copy, drawn by the same render engine the «Önizle» dialog
 * uses, with SAMPLE values rather than a recipient's (rule 12). So the author sees their document
 * — its words, its logo, its colours — and moves THAT, instead of a grey box with the word
 * «Sertifika» in it. The editor inlines it rather than putting it in an `<img>` on purpose: an
 * image document renders in an isolated context where the editor's loaded font faces do not
 * exist, and a certificate in the wrong typeface is not this certificate.
 *
 * It arrives here already built, as a node. This panel composes no markup at all — it positions a
 * box — and the drawing's injection stays next to the producer that made it, which is where the
 * raw-HTML audit can check the claim.
 *
 * The drawing is fitted, never stretched: it keeps its own aspect inside the box, exactly as the
 * server's composer letterboxes it, and resizing the box keeps the box's proportions too.
 *
 * WHAT IS EDITED, AND WHAT IS DRAWN
 * ---------------------------------
 * The canvas is a LAYOUT editor at the card's real proportions and the card's own coordinates. The
 * output is composed by the server, by the same composer the share uses, and «Önizle» is that
 * composition. One difference is worth stating and is stated on screen: the canvas draws the
 * working copy in this tab, while the server draws the draft as last SAVED — so an unsaved
 * certificate edit shows here before it shows there.
 *
 * NOTHING HERE POSTS. Arranging, previewing, saving and resetting touch no LinkedIn API, create no
 * record and mint no token.
 */
import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useV07Commands } from '../../data/commands-context';
import type { LibraryAsset } from '../../lib/asset-library';
import {
  type CardImageItem,
  type CardItem,
  type CardTextAlign,
  type CardTextItem,
  type LinkedInCardDesign,
  type LinkedInCardPreview,
  newItemId,
  type TemplateLinkedInCardPort,
} from '../../data/template-linkedin-card';

/** The card's own coordinate space. The panel scales it to fit; it never changes it. */
const CANVAS_W = 1200;
const CANVAS_H = 630;

/**
 * How the certificate's drawing is asked to size itself on this canvas.
 *
 * The render engine writes an explicit `width`/`height` in user units beside the `viewBox`, which
 * is what gives an SVG an intrinsic size — and exactly what stops it from shrinking into a box.
 * These declarations override both and cap them at the box, leaving the viewBox to keep the
 * aspect, so the drawing letterboxes itself the way the server's composer letterboxes it. It is
 * the root element's own sizing and nothing else: no content is rewritten.
 *
 * Exported because the injection lives in the editor, next to the producer.
 */
export const LINKEDIN_CARD_FIT = {
  /*
   * `responsive` off, because it writes its own `style` — and two `style` attributes on one tag
   * is not a merge: the parser keeps the first and drops the second. Measured: the drawing came
   * back 282×282 inside a 282×204 box, overflowing by 78px, because `width:100%;height:auto` won
   * and these declarations were discarded.
   */
  responsive: false,
  attrs:
    'preserveAspectRatio="xMidYMid meet" style="max-width:100%;max-height:100%;width:auto;height:auto;display:block"',
} as const;

export interface LinkedInCardPanelProps {
  /** The port, or null on a surface with no server behind it. */
  port: TemplateLinkedInCardPort | null;
  /** The certificate whose share image this is. */
  templateId: string | null;
  /** The editor's kept copy of unsaved work, so closing the dialog does not throw it away. */
  draft?: LinkedInCardDesign | null;
  onDraftChange?: (draft: LinkedInCardDesign | null) => void;
  /** The editor's own title carries the name; a heading here would say it twice. */
  heading?: string | null;
  /** The library, with the bytes the session is allowed to read, so an overlay can be drawn. */
  assets?: LibraryAsset[];
  /**
   * The certificate as the editor is drawing it right now, or null when it cannot be drawn.
   *
   * A NODE, not a string. The drawing is markup the render engine composed, and the rule that
   * guards raw HTML wants the injection to sit where the producer is named — so the editor builds
   * the element beside its own `previewSVG` call and this panel places it. That also keeps this
   * file free of any markup handling at all: it positions a box, it does not compose a document.
   */
  artwork?: ReactNode;
  /** True when the editor holds certificate edits the server has not been told about yet. */
  certificateUnsaved?: boolean;
  /**
   * The language this arrangement belongs to.
   *
   * A certificate sealed in two languages has TWO share images: the picture embeds that language's
   * certificate and the post beside it is in that language. Each is read, saved and reset on its
   * own, and neither falls back to the other. Omitted — every caller before locales, and every
   * single-language certificate — means the template's own declared language, which is what the
   * server answers for when no language is named.
   */
  lang?: 'tr' | 'en' | undefined;
  /** The panel's own language control reports here; the editor owns the working language. */
  onLang?: ((lang: 'tr' | 'en') => void) | undefined;
}

const STANDARD: LinkedInCardDesign = {
  version: 1,
  background: '#231A15',
  artwork: { x: 491, y: 58, width: 709, height: 514 },
  items: [],
};

type Selection = { kind: 'artwork' } | { kind: 'item'; id: string } | null;

type DragMode = 'move' | 'resize';

interface DragState {
  mode: DragMode;
  target: Selection;
  startX: number;
  startY: number;
  origin: { x: number; y: number; width: number; height: number };
}

const clone = (d: LinkedInCardDesign): LinkedInCardDesign => ({
  ...d,
  artwork: d.artwork ? { ...d.artwork } : null,
  items: d.items.map((i) => ({ ...i })),
});

/** What «is this the same arrangement?» means here: the values, not the object identity. */
const signature = (d: LinkedInCardDesign): string => JSON.stringify(d);

/** The design's own bounds, mirrored from the domain so the panel refuses before the server does. */
const MIN_SIZE = 8;
const MAX_SIZE = CANVAS_W * 2;
const clampSize = (n: number) => Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.round(n)));
const clampPos = (n: number) => Math.min(CANVAS_W * 2, Math.max(-CANVAS_W, Math.round(n)));

export function LinkedInCardPanel({
  port,
  templateId,
  draft,
  onDraftChange,
  heading,
  assets = [],
  artwork = null,
  certificateUnsaved = false,
  lang,
  onLang,
}: LinkedInCardPanelProps): ReactElement {
  const commands = useV07Commands();
  const [design, setDesign] = useState<LinkedInCardDesign>(() => clone(draft ?? STANDARD));
  const [custom, setCustom] = useState(false);
  const [selected, setSelected] = useState<Selection>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  /**
   * The last arrangement the SERVER is known to hold, as a signature.
   *
   * Everything about «unsaved» is derived from this one value rather than from a flag somebody has
   * to remember to clear. A save that lands while the author is still typing marks what it SENT as
   * saved, so the keystrokes that arrived after it stay unsaved instead of being counted as
   * written; a save that fails changes nothing, so the work is still theirs to retry.
   */
  const [savedSignature, setSavedSignature] = useState<string | null>(null);
  /**
   * The server's composition, and the arrangement it was composed FROM.
   *
   * Keeping the second half is the whole point: a picture of a layout the author has since changed
   * is not a preview of anything, and silently leaving it on screen is how somebody publishes a
   * card they never saw. It is not thrown away either — it is labelled as out of date.
   */
  const [preview, setPreview] = useState<{ value: LinkedInCardPreview; of: string } | null>(null);
  /**
   * The pictures this panel can DRAW, which is the library plus whatever was uploaded from here.
   *
   * Derived, not synchronised. An effect copying the prop into state looks harmless and is not:
   * the prop defaults to a fresh `[]` on every render, so the effect fired, set state, re-rendered
   * and fired again — the component never settled and the test run simply hung.
   */
  const [added, setAdded] = useState<LibraryAsset[]>([]);
  const surface = useRef<HTMLDivElement | null>(null);
  const drag = useRef<DragState | null>(null);
  /**
   * Which round trip is the current one.
   *
   * A slow save or preview that lands after a newer one must not overwrite it, and neither may
   * report its outcome over the newer request's. One counter answers both.
   */
  const trip = useRef(0);
  /**
   * Whether this panel is still on screen.
   *
   * A round trip started here can land after the dialog was closed, and `onDraftChange` belongs to
   * the EDITOR, not to this component — so unlike a `setState` it is not a harmless no-op after
   * unmount. A save or a reset whose answer arrived late would clear the unsaved arrangement the
   * author had meanwhile built in a REOPENED panel. Set in the effect body rather than at
   * declaration so React's extra StrictMode mount pass leaves it true.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  /** The newest arrangement, readable from a callback that started before it existed. */
  const latest = useRef(design);
  latest.current = design;
  /* The carried arrangement is an unsaved change from the first render, and a read must not win. */
  const dirtyBeforeRead = useRef(draft != null);

  const dirty = savedSignature === null ? false : signature(design) !== savedSignature;

  /* The saved arrangement, when there is a certificate to read one from. */
  useEffect(() => {
    if (!port || !templateId) return;
    let cancelled = false;
    void (async () => {
      try {
        const view = await port.read(templateId, lang);
        if (cancelled) return;
        setCustom(view.custom);
        setSavedSignature(signature(view.design));
        if (!dirtyBeforeRead.current) setDesign(clone(view.design));
      } catch (e) {
        if (!cancelled) setProblem(`Kayıtlı görsel okunamadı: ${(e as Error).message}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [port, templateId, lang]);

  const change = useCallback(
    (next: LinkedInCardDesign) => {
      /* A read that lands after the first edit must not land on top of it — the mistake the
         e-mail panel made once, kept fixed here. */
      dirtyBeforeRead.current = true;
      setDesign(next);
      setMessage(null);
      setProblem(null);
      setConfirmingReset(false);
      onDraftChange?.(next);
    },
    [onDraftChange],
  );

  const selectedItem: CardItem | null =
    selected && selected.kind === 'item' ? (design.items.find((i) => i.id === selected.id) ?? null) : null;

  const patchItem = useCallback(
    (id: string, patch: Partial<CardTextItem> & Partial<CardImageItem>) => {
      change({
        ...design,
        items: design.items.map((i) => (i.id === id ? ({ ...i, ...patch } as CardItem) : i)),
      });
    },
    [change, design],
  );

  // ------------------------------------------------------------------ moving and resizing

  const scaleOf = () => {
    const el = surface.current;
    return el ? el.clientWidth / CANVAS_W : 1;
  };

  const boxOf = (target: Selection): { x: number; y: number; width: number; height: number } | null => {
    if (!target) return null;
    if (target.kind === 'artwork') return design.artwork;
    const item = design.items.find((i) => i.id === target.id);
    if (!item) return null;
    if (item.kind === 'image') return item;
    /* A text box has a width and a position; its height follows the words, so the handle is one
       axis and the box reports the height the panel is drawing it at. */
    return { x: item.x, y: item.y, width: item.width, height: item.fontSize * 1.2 };
  };

  const beginDrag = (e: React.PointerEvent, target: Selection, mode: DragMode) => {
    const origin = boxOf(target);
    if (!origin) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setSelected(target);
    drag.current = { mode, target, startX: e.clientX, startY: e.clientY, origin: { ...origin } };
  };

  /**
   * The arrow keys, doing what the drag does.
   *
   * A layout tool whose only way to place something is a mouse drag is unusable without one, so
   * the same move is on the keyboard: one card unit a press, ten with Shift, and Escape lets go.
   */
  const nudge = (e: React.KeyboardEvent, target: Selection) => {
    if (e.key === 'Escape') {
      setSelected(null);
      return;
    }
    const step = e.shiftKey ? 10 : 1;
    const delta =
      e.key === 'ArrowLeft'
        ? [-step, 0]
        : e.key === 'ArrowRight'
          ? [step, 0]
          : e.key === 'ArrowUp'
            ? [0, -step]
            : e.key === 'ArrowDown'
              ? [0, step]
              : null;
    if (!delta || !target) return;
    e.preventDefault();
    const [dx, dy] = delta as [number, number];
    if (target.kind === 'artwork') {
      if (!design.artwork) return;
      change({
        ...design,
        artwork: {
          ...design.artwork,
          x: clampPos(design.artwork.x + dx),
          y: clampPos(design.artwork.y + dy),
        },
      });
      return;
    }
    const item = design.items.find((i) => i.id === target.id);
    if (!item) return;
    patchItem(item.id, { x: clampPos(item.x + dx), y: clampPos(item.y + dy) });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d?.target) return;
    const scale = scaleOf() || 1;
    const dx = (e.clientX - d.startX) / scale;
    const dy = (e.clientY - d.startY) / scale;
    if (d.target.kind === 'artwork') {
      if (!design.artwork) return;
      const next =
        d.mode === 'move'
          ? { ...design.artwork, x: clampPos(d.origin.x + dx), y: clampPos(d.origin.y + dy) }
          : /*
             * The document keeps its shape.
             *
             * The composer fits the drawing inside this box and letterboxes what is left over, so
             * a box of the wrong shape does not distort the certificate — it surrounds it with
             * dead space, which is worse to look at and impossible to aim. One handle drives both
             * sides through the box's own ratio, so what the author drags is the SIZE.
             */
            (() => {
              const ratio = d.origin.height / Math.max(1, d.origin.width);
              const width = clampSize(d.origin.width + dx);
              return { ...design.artwork, width, height: clampSize(width * ratio) };
            })();
      change({ ...design, artwork: next });
      return;
    }
    const item = design.items.find((i) => i.id === (d.target as { id: string }).id);
    if (!item) return;
    if (d.mode === 'move') {
      patchItem(item.id, { x: clampPos(d.origin.x + dx), y: clampPos(d.origin.y + dy) });
      return;
    }
    if (item.kind === 'image') {
      /* A logo keeps its shape too, and for the same reason. */
      const ratio = d.origin.height / Math.max(1, d.origin.width);
      const width = clampSize(d.origin.width + dx);
      patchItem(item.id, { width, height: clampSize(width * ratio) });
    } else patchItem(item.id, { width: clampSize(d.origin.width + dx) });
  };

  const endDrag = () => {
    drag.current = null;
  };

  // ------------------------------------------------------------------------------ the tools

  const addText = () => {
    const item: CardTextItem = {
      kind: 'text',
      id: newItemId('text'),
      x: 60,
      y: 60,
      width: 400,
      text: 'Yeni metin',
      fontSize: 44,
      color: '#FFFFFF',
      bold: true,
      align: 'left',
    };
    change({ ...design, items: [...design.items, item] });
    setSelected({ kind: 'item', id: item.id });
  };

  const addImage = async (file: File) => {
    setBusy(true);
    setProblem(null);
    try {
      const r = await commands.uploadAsset({ file });
      if (!r.ok || !r.asset) {
        setProblem(r.message ?? 'Görsel yüklenemedi.');
        return;
      }
      const asset = r.asset as LibraryAsset;
      setAdded((l) => (l.some((a) => a.id === asset.id) ? l : [...l, asset]));
      const w = 240;
      const ratio = asset.height > 0 ? asset.height / asset.width : 0.4;
      const item: CardImageItem = {
        kind: 'image',
        id: newItemId('image'),
        x: 60,
        y: 400,
        width: w,
        height: clampSize(w * ratio),
        assetId: asset.id,
      };
      /*
       * Against the CURRENT arrangement, not the one this upload started from.
       *
       * An upload is the slowest thing in this panel, and the author keeps working while it runs.
       * Closing over `design` would put the logo on the layout as it was a few seconds ago and
       * silently undo everything typed in between.
       */
      if (!alive.current) return;
      const base = latest.current;
      change({ ...base, items: [...base.items, item] });
      setSelected({ kind: 'item', id: item.id });
      setMessage('Görsel eklendi.');
    } catch (e) {
      setProblem(`Görsel yüklenemedi: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const removeSelected = () => {
    if (!selected) return;
    if (selected.kind === 'artwork') {
      change({ ...design, artwork: null });
      setSelected(null);
      return;
    }
    change({ ...design, items: design.items.filter((i) => i.id !== selected.id) });
    setSelected(null);
  };

  const move = (id: string, by: 1 | -1) => {
    const index = design.items.findIndex((i) => i.id === id);
    const to = index + by;
    if (index < 0 || to < 0 || to >= design.items.length) return;
    const items = [...design.items];
    const [item] = items.splice(index, 1);
    items.splice(to, 0, item as CardItem);
    change({ ...design, items });
  };

  // ------------------------------------------------------------------------ server round trips

  const onPreview = async () => {
    if (!port || !templateId) return;
    const sent = clone(design);
    const of = signature(sent);
    const mine = ++trip.current;
    setBusy(true);
    setProblem(null);
    try {
      const p = await port.preview(templateId, sent, lang);
      if (!alive.current || trip.current !== mine) return;
      setPreview({ value: p, of });
      const notes = [
        p.missingAssets.length > 0
          ? `${p.missingAssets.length} görsel kütüphanede bulunamadı ve çizilmedi.`
          : null,
        p.artworkProblem ? p.artworkProblem.message : null,
      ].filter((n): n is string => n !== null);
      if (notes.length > 0) setMessage(notes.join(' '));
    } catch (e) {
      if (!alive.current || trip.current !== mine) return;
      /* A refused preview takes the old picture down with it: a stale image beside a fresh error
         is the one arrangement nobody can read correctly. */
      setPreview(null);
      setProblem(`Önizleme alınamadı: ${(e as Error).message}`);
    } finally {
      if (trip.current === mine) setBusy(false);
    }
  };

  const onSave = async () => {
    if (!port || !templateId) return;
    const sent = clone(design);
    const mine = ++trip.current;
    setBusy(true);
    setProblem(null);
    try {
      const saved = await port.save(templateId, sent, lang);
      if (!alive.current || trip.current !== mine) return;
      setCustom(saved.custom);
      /*
       * What the SERVER now holds, read from its own answer rather than assumed to be the object
       * that was posted. The two are not the same thing by definition — the server parses and
       * normalises what it stores — and calling the sent object «saved» would report agreement
       * that had never been checked.
       */
      setSavedSignature(signature(saved.design));
      dirtyBeforeRead.current = false;
      if (signature(latest.current) === signature(sent)) {
        /* Nothing typed since, so the panel adopts exactly what is stored and «Kaydedildi» is
           true of what is on screen. Anything typed since stays the author's, and unsaved. */
        setDesign(clone(saved.design));
        onDraftChange?.(null);
      }
      setMessage(
        saved.missingAssets.length > 0
          ? `Kaydedildi. ${saved.missingAssets.length} görsel kütüphanede yok; LinkedIn paylaşımı bu hâliyle yapılamaz.`
          : 'Kaydedildi. LinkedIn paylaşımında bu görsel kullanılacak.',
      );
    } catch (e) {
      if (!alive.current || trip.current !== mine) return;
      /* Nothing is marked saved and nothing is cleared: the arrangement is still the author's. */
      setProblem(`Kaydedilemedi: ${(e as Error).message}. Düzenlemeniz duruyor, yeniden deneyebilirsiniz.`);
    } finally {
      if (trip.current === mine) setBusy(false);
    }
  };

  const onReset = async () => {
    setConfirmingReset(false);
    if (!port || !templateId) {
      change(clone(STANDARD));
      return;
    }
    const mine = ++trip.current;
    /* What was on screen when the author confirmed. Anything different when the answer lands is
       work they did afterwards, and confirming a reset is not permission to delete that. */
    const confirmed = signature(latest.current);
    setBusy(true);
    setProblem(null);
    try {
      const view = await port.reset(templateId, lang);
      if (!alive.current || trip.current !== mine) return;
      setCustom(false);
      setSavedSignature(signature(view.design));
      dirtyBeforeRead.current = false;
      setPreview(null);
      if (signature(latest.current) === confirmed) {
        setDesign(clone(view.design));
        onDraftChange?.(null);
        setMessage('Varsayılan otomatik görsele dönüldü.');
      } else {
        /*
         * The saved arrangement IS gone on the server — that is what was asked for and it
         * happened. What is refused is the second, unasked-for deletion: the edits made while the
         * request was in flight stay on the canvas, and stay unsaved, so «Kaydet» can keep them.
         */
        setMessage(
          'Varsayılan otomatik görsele dönüldü. Bu sırada yaptığınız düzenleme duruyor ve kaydedilmedi.',
        );
      }
    } catch (e) {
      if (!alive.current || trip.current !== mine) return;
      setProblem(`Geri alınamadı: ${(e as Error).message}`);
    } finally {
      if (trip.current === mine) setBusy(false);
    }
  };

  const assetById = useMemo(() => new Map([...assets, ...added].map((a) => [a.id, a])), [assets, added]);
  const missingNow = useMemo(
    () => [
      ...new Set(
        design.items
          .filter((i): i is CardImageItem => i.kind === 'image')
          .filter((i) => !assetById.get(i.assetId)?.dataUrlForPrototype)
          .map((i) => i.assetId),
      ),
    ],
    [design.items, assetById],
  );
  const previewStale = preview !== null && preview.of !== signature(design);

  // ------------------------------------------------------------------------------- rendering

  if (!port) {
    return (
      <section className="stack" data-testid="linkedin-card-panel">
        {heading ? <h2>{heading}</h2> : null}
        <div className="info-box">
          Bu yüzeyde sunucu yok; LinkedIn paylaşım görseli burada düzenlenemiyor.
        </div>
      </section>
    );
  }

  return (
    <section className="stack" data-testid="linkedin-card-panel">
      {heading ? <h2>{heading}</h2> : null}
      {/*
       * THE LANGUAGE THIS ARRANGEMENT BELONGS TO.
       *
       * Two languages, two share images, saved on their own: the picture carries that language's
       * certificate and the post beside it is in that language. The pressed one is which arrangement
       * is on the canvas. It is the EDITOR's working language — the same choice as the head's, shown
       * here as well, so the panel and the certificate behind it can never disagree about it.
       */}
      {onLang && lang ? (
        <div className="row" data-testid="linkedin-card-lang">
          <span className="small muted">Bu paylaşım görselinin dili:</span>
          {(['tr', 'en'] as const).map((l) => (
            <button
              key={l}
              className={`btn btn-sm ${lang === l ? 'btn-primary' : 'btn-ghost'}`}
              type="button"
              id={l === 'tr' ? 'licLangTr' : 'licLangEn'}
              data-testid={`linkedin-card-lang-${l}`}
              aria-pressed={lang === l}
              disabled={busy}
              onClick={() => onLang(l)}
            >
              {l === 'tr' ? 'Türkçe' : 'English'}
            </button>
          ))}
        </div>
      ) : null}
      <p className="small muted">
        Bu görsel yalnız LinkedIn paylaşımında kullanılır. Sertifikanın kendisi, indirilen belge ve herkese
        açık sayfadaki bağlantı kartı değişmez. Bir tasarım kaydetmezseniz bugünkü otomatik görsel aynen
        kullanılmaya devam eder.
        {lang
          ? ` Bu tasarım yalnız ${lang === 'en' ? 'English' : 'Türkçe'} paylaşım için geçerlidir; diğer dilin tasarımı ayrı kaydedilir ve bu kaydetme onu değiştirmez.`
          : ''}
      </p>

      {templateId === null ? (
        <div className="info-box" data-testid="linkedin-card-no-template">
          Sertifika henüz oluşturulmadı: yerleşimi burada hazırlayabilirsiniz, sertifikanın kendisi ve sunucu
          önizlemesi editörde görünür. Yazdıklarınız kaybolmaz.
        </div>
      ) : null}

      <div className="toolbar" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <label className="field" style={{ margin: 0 }}>
          <span className="small">Arka plan</span>
          <input
            type="color"
            id="licBackground"
            data-testid="linkedin-card-background"
            aria-label="Arka plan rengi"
            value={design.background}
            onChange={(e) => change({ ...design, background: e.target.value.toUpperCase() })}
          />
        </label>
        <button
          className="btn btn-secondary"
          type="button"
          data-testid="linkedin-card-add-text"
          onClick={addText}
        >
          Metin ekle
        </button>
        <label className="btn btn-secondary" style={{ margin: 0 }}>
          Görsel ekle
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml"
            data-testid="linkedin-card-add-image"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void addImage(file);
            }}
          />
        </label>
        {design.artwork === null ? (
          <button
            className="btn btn-secondary"
            type="button"
            data-testid="linkedin-card-restore-artwork"
            onClick={() => change({ ...design, artwork: { ...(STANDARD.artwork as object) } as never })}
          >
            Sertifikayı geri koy
          </button>
        ) : null}
        <button
          className="btn btn-ghost"
          type="button"
          data-testid="linkedin-card-remove"
          disabled={selected === null}
          onClick={removeSelected}
        >
          Seçileni kaldır
        </button>
        {selectedItem ? (
          <>
            <button
              className="btn btn-ghost"
              type="button"
              data-testid="linkedin-card-forward"
              onClick={() => move(selectedItem.id, 1)}
            >
              Öne getir
            </button>
            <button
              className="btn btn-ghost"
              type="button"
              data-testid="linkedin-card-backward"
              onClick={() => move(selectedItem.id, -1)}
            >
              Arkaya gönder
            </button>
          </>
        ) : null}
      </div>

      {/* ------------------------------------------------------------------- the layout canvas */}
      <div
        ref={surface}
        className="lic-canvas"
        data-testid="linkedin-card-canvas"
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
          background: design.background,
          overflow: 'hidden',
          border: '1px solid var(--line, #d9d5d1)',
          touchAction: 'none',
          /*
           * ONE container, and it is the card.
           *
           * Every size on this canvas is expressed in card units and shown as a fraction of the
           * card's width, so the query container has to BE the card. It used to be each piece:
           * `fontSize` was written as `cqw` while the container was the text box, so a 56-unit
           * caption drew at 18.67px in a 400-unit box and at 28px in a 600-unit one — dragging a
           * box wider made its words smaller, and neither number was the size the server would
           * use. With the container here, `(fontSize / 1200) * 100cqw` is the card fraction it
           * says it is, and widening a box moves the wrap, not the type.
           */
          containerType: 'inline-size',
        }}
        /* A drawing surface, operated with a pointer: `application` is what a screen reader
           needs to hand the arrow keys and the drag to the canvas rather than to the page. Every
           piece on it is a focusable group with its own name, and Escape clears the selection. */
        role="application"
        aria-label="LinkedIn paylaşım görseli tuvali"
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {design.artwork ? (
          <Box
            box={design.artwork}
            selected={selected?.kind === 'artwork'}
            testId="linkedin-card-artwork"
            label="Sertifika"
            onPointerDown={(e) => beginDrag(e, { kind: 'artwork' }, 'move')}
            onNudge={(e) => nudge(e, { kind: 'artwork' })}
            onResize={(e) => beginDrag(e, { kind: 'artwork' }, 'resize')}
          >
            {artwork ? (
              /*
               * The document itself, as the editor drew it, and inert.
               *
               * `pointer-events: none` matters: the handles sit on top of this, and a drawing that
               * answered the pointer would swallow the drag the author is trying to start.
               */
              artwork
            ) : (
              <span
                className="small"
                data-testid="linkedin-card-artwork-unavailable"
                style={{ color: '#ffffff', opacity: 0.85, padding: '4px' }}
              >
                Sertifika çizilemedi
              </span>
            )}
          </Box>
        ) : null}

        {design.items.map((item) => {
          const isSelected = selected?.kind === 'item' && selected.id === item.id;
          if (item.kind === 'image') {
            const asset = assetById.get(item.assetId);
            return (
              <Box
                key={item.id}
                box={item}
                selected={isSelected}
                testId={`linkedin-card-item-${item.id}`}
                label="Görsel"
                onPointerDown={(e) => beginDrag(e, { kind: 'item', id: item.id }, 'move')}
                onNudge={(e) => nudge(e, { kind: 'item', id: item.id })}
                onResize={(e) => beginDrag(e, { kind: 'item', id: item.id }, 'resize')}
              >
                {asset?.dataUrlForPrototype ? (
                  <img
                    src={asset.dataUrlForPrototype}
                    alt=""
                    data-testid={`linkedin-card-image-${item.id}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      pointerEvents: 'none',
                    }}
                  />
                ) : (
                  <span className="small" style={{ color: '#fff' }}>
                    Görsel yüklenemedi
                  </span>
                )}
              </Box>
            );
          }
          return (
            <Box
              key={item.id}
              box={{ x: item.x, y: item.y, width: item.width, height: item.fontSize * 1.3 }}
              selected={isSelected}
              testId={`linkedin-card-item-${item.id}`}
              label="Metin"
              autoHeight
              onPointerDown={(e) => beginDrag(e, { kind: 'item', id: item.id }, 'move')}
              onNudge={(e) => nudge(e, { kind: 'item', id: item.id })}
              onResize={(e) => beginDrag(e, { kind: 'item', id: item.id }, 'resize')}
            >
              <span
                data-testid={`linkedin-card-text-${item.id}`}
                style={{
                  display: 'block',
                  width: '100%',
                  color: item.color,
                  fontWeight: item.bold ? 700 : 400,
                  fontSize: `${(item.fontSize / CANVAS_W) * 100}cqw`,
                  lineHeight: 1.2,
                  textAlign: item.align,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                  pointerEvents: 'none',
                }}
              >
                {item.text}
              </span>
            </Box>
          );
        })}
      </div>
      <p className="small muted" data-testid="linkedin-card-canvas-note">
        {certificateUnsaved
          ? 'Tuvaldeki sertifika bu sekmedeki güncel tasarımınızdır (örnek değerlerle). Sunucu önizlemesi en son KAYDEDİLEN taslağı çizer; sertifika tasarımınızda kaydedilmemiş değişiklikler var.'
          : 'Tuvaldeki sertifika bu sekmedeki tasarımınızdır (örnek değerlerle); kesin görünüm sunucunun ürettiği önizlemedir.'}
      </p>
      {missingNow.length > 0 ? (
        <div className="warn-box" role="alert" data-testid="linkedin-card-missing-assets">
          {`${missingNow.length} görsel kütüphaneden okunamadı ve tuvalde çizilemiyor. Bu hâliyle LinkedIn paylaşımı yapılamaz; görseli yeniden yükleyin ya da kaldırın.`}
        </div>
      ) : null}

      {/* --------------------------------------------------------------- the selected item's own */}
      {selectedItem && selectedItem.kind === 'text' ? (
        <div className="card-pad stack" data-testid="linkedin-card-text-tools">
          <label className="field">
            <span className="small">Metin</span>
            <textarea
              className="input"
              rows={2}
              data-testid="linkedin-card-text-value"
              value={selectedItem.text}
              onChange={(e) => patchItem(selectedItem.id, { text: e.target.value })}
            />
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'end' }}>
            <label className="field" style={{ margin: 0 }}>
              <span className="small">Yazı boyutu</span>
              <input
                className="input"
                type="number"
                min={8}
                max={200}
                data-testid="linkedin-card-font-size"
                value={selectedItem.fontSize}
                onChange={(e) =>
                  patchItem(selectedItem.id, {
                    fontSize: Math.min(200, Math.max(8, Number(e.target.value) || 8)),
                  })
                }
              />
            </label>
            <label className="field" style={{ margin: 0 }}>
              <span className="small">Yazı rengi</span>
              <input
                type="color"
                data-testid="linkedin-card-text-color"
                aria-label="Yazı rengi"
                value={selectedItem.color}
                onChange={(e) => patchItem(selectedItem.id, { color: e.target.value.toUpperCase() })}
              />
            </label>
            <label className="field" style={{ margin: 0 }}>
              <span className="small">Kalın</span>
              <input
                type="checkbox"
                data-testid="linkedin-card-bold"
                checked={selectedItem.bold}
                onChange={(e) => patchItem(selectedItem.id, { bold: e.target.checked })}
              />
            </label>
            <label className="field" style={{ margin: 0 }}>
              <span className="small">Hizalama</span>
              <select
                className="input"
                data-testid="linkedin-card-align"
                value={selectedItem.align}
                onChange={(e) => patchItem(selectedItem.id, { align: e.target.value as CardTextAlign })}
              >
                <option value="left">Sol</option>
                <option value="center">Orta</option>
                <option value="right">Sağ</option>
              </select>
            </label>
          </div>
        </div>
      ) : null}

      {/* ---------------------------------------------------------------------------- the actions */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          className="btn btn-secondary"
          type="button"
          data-testid="linkedin-card-preview"
          disabled={busy || templateId === null}
          onClick={() => void onPreview()}
        >
          Önizle
        </button>
        <button
          className="btn btn-primary"
          type="button"
          data-testid="linkedin-card-save"
          disabled={busy || templateId === null}
          onClick={() => void onSave()}
        >
          Kaydet
        </button>
        {/*
         * «Varsayılan otomatik görsele dön» throws work away, so it asks first.
         *
         * Two presses, in the panel rather than in a browser dialog: the second one names what is
         * about to be lost, which a generic confirm box cannot do.
         */}
        {confirmingReset ? (
          <>
            <button
              className="btn btn-danger"
              type="button"
              data-testid="linkedin-card-reset-confirm"
              disabled={busy}
              onClick={() => void onReset()}
            >
              {custom
                ? 'Evet, kayıtlı görseli sil'
                : dirty
                  ? 'Evet, düzenlememi sil'
                  : 'Evet, varsayılana dön'}
            </button>
            <button
              className="btn btn-ghost"
              type="button"
              data-testid="linkedin-card-reset-cancel"
              onClick={() => setConfirmingReset(false)}
            >
              Vazgeç
            </button>
          </>
        ) : (
          <button
            className="btn btn-ghost"
            type="button"
            data-testid="linkedin-card-reset"
            disabled={busy}
            onClick={() => {
              /* Nothing saved and nothing typed: there is nothing to confirm away. */
              if (!custom && !dirty) void onReset();
              else setConfirmingReset(true);
            }}
          >
            Varsayılan otomatik görsele dön
          </button>
        )}
      </div>

      <p className="small muted" data-testid="linkedin-card-state">
        {custom
          ? 'Bu sertifika için özel bir LinkedIn görseli kayıtlı.'
          : 'Kayıtlı özel görsel yok; otomatik görsel kullanılıyor.'}
        {dirty ? ' Kaydedilmemiş değişiklikleriniz var.' : ''}
      </p>

      {problem ? (
        <div className="warn-box" role="alert" data-testid="linkedin-card-problem">
          {problem}
        </div>
      ) : null}
      {message ? (
        <div className="info-box" role="status" data-testid="linkedin-card-message">
          {message}
        </div>
      ) : null}

      {preview ? (
        <figure className="stack" data-testid="linkedin-card-preview-figure">
          <img
            src={preview.value.imageDataUri}
            alt="LinkedIn paylaşım görseli önizlemesi"
            data-testid="linkedin-card-preview-image"
            style={{
              width: '100%',
              border: '1px solid var(--line, #d9d5d1)',
              opacity: previewStale ? 0.55 : 1,
            }}
          />
          <figcaption className="small muted" data-testid="linkedin-card-preview-caption">
            {previewStale
              ? `ESKİ önizleme — yerleşimi bundan sonra değiştirdiniz · ${preview.value.width}×${preview.value.height}`
              : `Sunucunun ürettiği görsel · ${preview.value.width}×${preview.value.height}`}
            {preview.value.artworkIncluded ? '' : ' · sertifika çizilemedi'}
          </figcaption>
        </figure>
      ) : null}
    </section>
  );
}

/** One draggable box on the canvas, positioned in the card's own coordinates. */
function Box({
  box,
  selected,
  label,
  testId,
  children,
  autoHeight,
  onPointerDown,
  onNudge,
  onResize,
}: {
  box: { x: number; y: number; width: number; height: number };
  selected: boolean;
  label: string;
  testId: string;
  children: React.ReactNode;
  autoHeight?: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  onNudge: (e: React.KeyboardEvent) => void;
  onResize: (e: React.PointerEvent) => void;
}): ReactElement {
  return (
    /*
     * A plain positioned wrapper, deliberately with no role of its own: the two buttons inside it
     * carry the accessible names («… konumu», «… boyutunu değiştir»), and a `role="group"` here
     * would add a landmark that names the piece twice and answers to neither key.
     *
     * No `containerType` here either — the canvas is the container, and giving each box one is
     * exactly what made a text box's own width change the size of its words.
     */
    <div
      data-testid={testId}
      data-x={box.x}
      data-y={box.y}
      data-width={box.width}
      data-height={box.height}
      style={{
        position: 'absolute',
        left: `${(box.x / CANVAS_W) * 100}%`,
        top: `${(box.y / CANVAS_H) * 100}%`,
        width: `${(box.width / CANVAS_W) * 100}%`,
        ...(autoHeight ? {} : { height: `${(box.height / CANVAS_H) * 100}%` }),
        outline: selected ? '2px solid #0A66C2' : '1px dashed rgba(255,255,255,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
      }}
    >
      {children}
      <button
        type="button"
        aria-label={`${label} konumu`}
        data-testid={`${testId}-move`}
        onPointerDown={onPointerDown}
        onKeyDown={onNudge}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'transparent',
          border: 0,
          padding: 0,
          cursor: 'move',
        }}
      />
      <button
        type="button"
        aria-label={`${label} boyutunu değiştir`}
        data-testid={`${testId}-resize`}
        onPointerDown={onResize}
        style={{
          position: 'absolute',
          right: '-6px',
          bottom: '-6px',
          width: '14px',
          height: '14px',
          background: '#0A66C2',
          border: '1px solid #fff',
          borderRadius: '2px',
          padding: 0,
          cursor: 'nwse-resize',
        }}
      />
    </div>
  );
}
